import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Eye, Check } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";

export const AuditTab = () => {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="glass overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-audit-table">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Zeit", "Akteur", "Aktion", "Ziel", "Details"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {logs.length === 0 && <tr><td className="px-4 py-6 text-slate-500" colSpan={5}>Noch keine Admin-Aktionen.</td></tr>}
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-white/5">
              <td className="px-4 py-2 whitespace-nowrap text-slate-400">{fmtDate(l.ts)}</td><td className="px-4 py-2 text-slate-300">{l.actor}</td>
              <td className="px-4 py-2 font-mono text-xs text-blue-300">{l.action}</td><td className="px-4 py-2 text-slate-300 break-all">{l.target}</td>
              <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{typeof l.details === "string" ? l.details : JSON.stringify(l.details)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SAMPLE = '[\n  {"id": "example_ai", "name": "Example AI", "api_base": "https://api.example-ai.com/v1", "docs_url": "https://example-ai.com/docs", "cost_status": "unknown", "source": "manuell"}\n]';

export const ImportTab = () => {
  const [text, setText] = useState(SAMPLE);
  const [res, setRes] = useState(null);
  const send = async (apply) => {
    let entries;
    try { entries = JSON.parse(text); } catch { toast.error("Kein gültiges JSON"); return; }
    if (!Array.isArray(entries)) { toast.error("Erwartet wird eine JSON-Liste"); return; }
    try { const { data } = await api.post("/admin/catalog/import", { entries, apply }); setRes(data); if (apply) toast.success("Import übernommen"); } catch (e) { toast.error(errMsg(e)); }
  };
  const d = res?.diff;
  return (
    <div className="glass space-y-4 p-6">
      <p className="text-sm text-slate-400">Öffentliche Katalogdaten (JSON-Liste) importieren. Nur https-Basis-URLs, Deduplizierung nach ID und Host. Importierte Einträge sind reines Inventar – sie werden <b>nicht</b> geroutet, bis ein Adapter implementiert und geprüft ist.</p>
      <textarea data-testid="import-json-input" rows={8} className="field font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex gap-2">
        <button onClick={() => send(false)} data-testid="import-preview-button" className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white"><Eye className="h-4 w-4" />Diff-Vorschau</button>
        <button onClick={() => send(true)} disabled={!d} data-testid="import-apply-button" className="btn-grad inline-flex items-center gap-1.5 px-4 py-2 text-sm"><Check className="h-4 w-4" />Übernehmen</button>
      </div>
      {d && (
        <div className="grid gap-3 md:grid-cols-4 text-xs" data-testid="import-diff">
          {[["new", "Neu", "text-emerald-300"], ["changed", "Geändert", "text-amber-300"], ["duplicate", "Dublette", "text-slate-300"], ["invalid", "Ungültig", "text-red-300"]].map(([k, l, c]) => (
            <div key={k} className="rounded-lg border border-white/5 p-3" data-testid={`import-diff-${k}`}>
              <div className={`mb-2 font-semibold ${c}`}>{l}: {d[k].length}</div>
              <ul className="space-y-1 text-slate-400">{d[k].map((e, i) => <li key={i} className="break-all">{e.id || e.entry}{e.reason ? ` – ${e.reason}` : ""}</li>)}</ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const EmailTab = () => {
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [result, setResult] = useState("");
  useEffect(() => { api.get("/admin/email/status").then((r) => setSt(r.data)); }, []);
  const test = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/email/test", { to }); setResult(data.status); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!st) return null;
  return (
    <div className="glass space-y-4 p-6" data-testid="admin-email-tab">
      <div data-testid="email-config-status" className={`rounded-lg border px-4 py-3 text-sm ${st.configured ? "border-emerald-500/30 text-emerald-200" : "border-amber-500/40 bg-amber-500/10 text-amber-200"}`}>
        {st.configured ? `SMTP konfiguriert: ${st.host}:${st.port} (${st.security}) · Absender ${st.from}` : "SMTP ist nicht konfiguriert. Reset- und Bestätigungs-E-Mails werden NICHT versendet. Benötigt: SMTP_HOST, SMTP_PORT, SMTP_USERNAME, SMTP_PASSWORD, SMTP_FROM, SMTP_SECURITY."}
      </div>
      <form onSubmit={test} className="flex flex-col gap-3 md:flex-row">
        <input required type="email" data-testid="email-test-to" className="field md:w-80" placeholder="Empfänger für Test-E-Mail" value={to} onChange={(e) => setTo(e.target.value)} />
        <button data-testid="email-test-button" className="btn-grad inline-flex items-center gap-1.5 px-4 py-2 text-sm"><Send className="h-4 w-4" />Test-E-Mail senden</button>
      </form>
      {result && <div data-testid="email-test-result" className={`text-sm ${result === "sent" ? "text-emerald-300" : "text-red-300"}`}>{{ sent: "Vom SMTP-Server angenommen – bitte Postfach prüfen.", not_configured: "Nicht gesendet: SMTP nicht konfiguriert.", failed: "Versand fehlgeschlagen (siehe Server-Log, ohne Zugangsdaten)." }[result]}</div>}
    </div>
  );
};
